import { useState, memo } from 'react';
import { Field, FieldProps, FormikContextType, useField, useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { type AppState } from '@nextgen-web-framework/core';
import {
  FAIcon,
  IconStyle,
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { DynamicObjectInterface, RegFlowConfig } from '@commerce-profile-webcomponents/sdk';
import DropDownwithMasterData from '../DropDownwithMasterData/DropDownwithMasterData';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { useCountryMasterData } from '../../utils/masterdata';
import { DropDownSelectType } from '../DropDownwithMasterData/types/propTypes';
import { PersonalIdAndTypeInputProps, DocumentTypeIdValueProps } from './types/propTypes';
import styles from './PersonalIdAndTypeInput.module.scss';

const PersonalIdAndTypeInput = ({
  isRefreshRestoredData,
  nextHandlerCallback,
  resetPersonalForm,
  mlpVersionProp = MLPVersion.TWO,
  wrapperClass,
}: PersonalIdAndTypeInputProps) => {
  const [idLoader, setIdLoader] = useState(false);
  const [isIdVerified, setIsIdVerified] = useState(isRefreshRestoredData ?? false);
  const t = useTranslation();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const [, , idTypeHandler] = useField('personalIdType');
  const [, , idRecordNumberHandler] = useField('personalIdRecordNumber');
  const { values, status, setStatus, handleBlur, setFieldValue }: FormikContextType<DocumentTypeIdValueProps> =
    useFormikContext();
  const { currentCountry } = useCountryMasterData();
  const { userData, perosonalDetails, flowName } = registrationFlowData;
  const { patchVersion } = userData;
  const configs = ConfigService.getConfigs();
  const mlpVersionConfig = configs?.NEXT_INPUT_MLP_VERSION as MLPVersion;
  const mlpVersion = mlpVersionConfig ?? mlpVersionProp;
  const { isAnonymous } = useSession();
  const isCoappDownlineFlow = flowName === RegFlowConfig.CoApplicantFlow && isAnonymous();
  const onIDVerifyClick = async () => {
    if (values?.personalIdRecordNumber) {
      setIdLoader(true);
      const payload = {
        personalIdList: [
          {
            recordType: values?.personalIdType,
            recordNumber: values?.personalIdRecordNumber,
          },
        ],
        version: patchVersion,
      };

      try {
        const res: DynamicObjectInterface = await nextHandlerCallback(payload);
        setIsIdVerified(true); //to limit api calls
        if (res?.registrationId) {
          setStatus({ enableField: true });
          setFieldValue('citizenshipCountry', perosonalDetails.citizenshipCountry || currentCountry?.pk);
        }
        if (res?.responseBody?.code) {
          idRecordNumberHandler.setError(`profile.RegistrationCIDVerification.${res?.responseBody?.code}`);
          setStatus({ enableField: false });
        }
      } catch {
        setStatus({ enableField: false });
      } finally {
        setIdLoader(false);
      }
    }
  };

  const onIDBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isIdVerified) {
      handleBlur(e);
    }
  };

  const onIdChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const idType = values?.personalIdType;
    resetPersonalForm();
    setIsIdVerified(false);
    setStatus({ enableField: false });
    idTypeHandler.setValue(idType);
    idRecordNumberHandler.setValue(e.target.value);
  };

  const personalIdChangeCallback = async (value: string) => {
    resetPersonalForm();
    setIsIdVerified(false);
    await idTypeHandler.setValue(value);
  };
  return (
    <>
      <div className={`${styles['input-fields']} ${wrapperClass}`}>
        <Field type="text" name="personalIdType">
          {({ field }: FieldProps) => (
            <DropDownwithMasterData
              fieldName="personalIdType"
              value={field.value}
              onSelect={personalIdChangeCallback}
              mlpVersionProp={mlpVersion}
              dropDownSelectType={
                mlpVersion === MLPVersion.ONE ? DropDownSelectType.DROPDOWN : DropDownSelectType.DRAWER
              }
              disabled={isCoappDownlineFlow}
            />
          )}
        </Field>
      </div>
      <div className={`${styles['input-fields']} ${wrapperClass}`}>
        <Field type="text" name="personalIdRecordNumber">
          {({ meta, field }: FieldProps) => (
            <>
              <div className={styles.inputContainer}>
                <div className={styles.inputBox}>
                  <Input
                    id="personalIdRecordNumber"
                    data-testid="personalIdRecordNumber"
                    type={InputTypes.TEXT}
                    name="personalIdRecordNumber"
                    value={field.value}
                    label={t('profile.ProfileInfo.personalIdRecordNumber')}
                    onChange={(e) => onIdChangeHandler(e)}
                    onBlur={(e) => onIDBlur(e)}
                    mlpVersion={mlpVersion}
                    disabled={!values?.personalIdType || isCoappDownlineFlow}
                    autoComplete="off"
                    ctaTextLink={
                      !isCoappDownlineFlow
                        ? {
                            text: t('profile.SinglePageRegistrationStep.verify'),
                            id: 'btn_verify',
                            hidden: idLoader,
                            customClass: styles['input-link'],
                            onClick: onIDVerifyClick,
                            disabled: !field.value || isIdVerified || idLoader,
                            theme: Theme.PURPLE,
                          }
                        : undefined
                    }
                    inputIcon={idLoader ? { iconName: 'spinner-third', iconStyle: IconStyle.SOLID } : undefined}
                    iconClassName={idLoader ? 'fa-spin' : ''}
                    status={inputErrorStatus(meta.touched, meta.error)}
                    message={inputErrorMessage(t, meta.touched, meta.error)}
                  />
                </div>
                {(mlpVersion === MLPVersion.ONE || isCoappDownlineFlow) && (
                  <button
                    className={styles.ctaButton}
                    data-testid="verify-button"
                    onClick={onIDVerifyClick}
                    disabled={!field.value || isIdVerified || idLoader}
                  >
                    {t('profile.SinglePageRegistrationStep.verify')}
                  </button>
                )}
              </div>
              {isIdVerified && !meta.error && status?.enableField && (
                <span
                  role="presentation"
                  aria-label="message"
                  className={`${styles['float-input-message']}`}
                  data-testid="message-id"
                >
                  <FAIcon iconName={'circle-check'} theme={Theme.GREEN} className={styles['input-icon']} />
                  <span className={styles['float-message']}>{t('profile.documentIdInput.recordNumberVerified')}</span>
                </span>
              )}
            </>
          )}
        </Field>
      </div>
    </>
  );
};
export default memo(PersonalIdAndTypeInput);
